<template>
    <UCard variant="subtle" :class="cardBlockUi.root" :ui="cardBlockUi.card">
        <div :class="cardBlockUi.row">
            <UIcon v-if="showIcon" :name="iconName" size="xl" :class="cardBlockUi.icon" />
            <div :class="cardBlockUi.content">
                <template v-for="content in config.content" :key="content.id">
                    <TextBlock v-if="content.type === 'text'" :config="content"></TextBlock>
                    <LinesBlock v-else-if="content.type === 'lines'" :config="content"></LinesBlock>
                    <ButtonBlock
                        v-else-if="content.type === 'button'"
                        :config="content"
                    ></ButtonBlock>
                    <ImageBlock v-else-if="content.type === 'image'" :config="content"></ImageBlock>
                    <IconBlock v-else-if="content.type === 'icon'" :config="content"></IconBlock>
                    <SectionBlock
                        v-else-if="content.type === 'section'"
                        :config="content"
                    ></SectionBlock>
                    <RowBlock v-else-if="content.type === 'row'" :config="content"></RowBlock>
                    <AdditionalColorsPricingBlock
                        v-else-if="content.type === 'additional-colors-pricing'"
                        :config="content"
                    />
                </template>
            </div>
        </div>
    </UCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SectionBlock from './SectionBlock.vue'
import TextBlock from './TextBlock.vue'
import LinesBlock from './LinesBlock.vue'
import ButtonBlock from './ButtonBlock.vue'
import ImageBlock from './ImageBlock.vue'
import IconBlock from './IconBlock.vue'
import RowBlock from './RowBlock.vue'
import AdditionalColorsPricingBlock from './AdditionalColorsPricingBlock.vue'
import type { CardBlockConfig } from '~~/layers/base/types/blockConfigs'

/** UCard slot map — optional Part B overrides from callers (e.g. AuthDrawer body pad). */
export type CardBlockUCardUi = {
    root?: string
    body?: string
    header?: string
    footer?: string
}

const props = withDefaults(
    defineProps<{
        config: CardBlockConfig
        /**
         * Partial UCard `:ui` overrides merged over defaults.
         * Named `ui` to match Nuxt UI; the layout map below is `cardBlockUi` (no clash).
         */
        ui?: Partial<CardBlockUCardUi>
    }>(),
    {
        ui: () => ({}),
    }
)

/** 1px border at 30% of currentColor (variant text color). */
const VARIANT_BORDER = 'border border-[color-mix(in_srgb,currentColor_30%,transparent)]'

const VARIANT_CLASS: Record<NonNullable<CardBlockConfig['variant']>, string> = {
    info: `bg-(--color-info-50) text-(--color-info-700) ${VARIANT_BORDER}`,
    infoDark: 'bg-(--bg-primary) text-(--ui-text-white)',
    success: `bg-(--color-success-50) text-(--color-success-700) ${VARIANT_BORDER}`,
    warning: `bg-(--color-warning-50) text-(--color-warning-700) ${VARIANT_BORDER}`,
    error: `bg-(--color-error-50) text-(--color-error-700) ${VARIANT_BORDER}`,
}

const DEFAULT_ICONS: Partial<Record<NonNullable<CardBlockConfig['variant']>, string>> = {
    info: 'i-lucide-info',
    success: 'i-lucide-circle-check',
    warning: 'i-lucide-triangle-alert',
    error: 'i-lucide-octagon-alert',
}

const iconName = computed(() => {
    if (props.config.icon === false) return ''
    if (typeof props.config.icon === 'string') return props.config.icon
    if (!props.config.variant) return ''
    return DEFAULT_ICONS[props.config.variant] || ''
})

const showIcon = computed(() => {
    if (props.config.icon === false) return false
    return !!props.config.variant || !!props.config.icon
})

/**
 * UI map: root / row / icon / content classes + merged UCard `:ui`.
 * Keeps layout out of the template (computed-ui-pattern / vue-ui-patterns Part A + B).
 */
const cardBlockUi = computed(() => {
    const variant = props.config.variant ? (VARIANT_CLASS[props.config.variant] ?? '') : ''

    return {
        // UCard root class list (ring-0 clears Nuxt subtle ring)
        root: ['ring-0 rounded', variant, props.config.ui].filter(Boolean).join(' '),
        // Inner flex row (icon + body)
        row: 'flex gap-2',
        icon: 'w-5.5 h-5.5',
        content: 'flex-1 min-w-0',
        // Part B: caller `ui` prop wins per slot
        card: {
            root: props.ui.root,
            body: props.ui.body,
            header: props.ui.header,
            footer: props.ui.footer,
        } satisfies CardBlockUCardUi,
    }
})
</script>
